<template>
  <MRow
    :gutter="0"
    :class="{
      // 'bg-neutral-lightest': isDone && markBgColor,
      // 'task-closed': isDone && markBgColor,
    }"
    class="task-item"
  >
    <MCol>
      <MRow>
        <MCol>
          <div class="heading flex flex-1 items-center mb-2">
            <div class="flex-1 items-center align-items-center flex">
              <span
                v-if="allowTaskHeader"
                class="task-name-pill flex items-center mr-2 p-2 whitespace-no-wrap"
              >
                <MIcon :name="'tasks'" :size="lg" class="mr-1" />
                {{ task.name }}
              </span>
              <span
                v-if="allowTaskHeader"
                style="max-width: calc(100vw - 650px)"
                class="mr-2 whitespace-no-wrap overflow-hidden text-ellipsis inline-block"
              >
                <MTooltip>
                  <template v-slot:trigger>
                    <span> {{ task.subject }} </span>
                  </template>
                  {{ task.subject }}
                </MTooltip>
              </span>
              <MTooltip
                v-if="
                  allowEdit &&
                  !disabled &&
                  !isDone &&
                  canUpdateTask(task.moduleName)
                "
              >
                <template v-slot:trigger>
                  <MButton
                    class="ml-2 icon-button rounded"
                    variant="default"
                    @click="$emit('edit', task)"
                  >
                    <MIcon
                      :id="`edit-btn-${task.name}:${task.subject}`"
                      variant="default"
                      name="pencil"
                      size="lg"
                    />
                  </MButton>
                </template>
                {{ $t('edit') }}
              </MTooltip>
              <slot name="task-actions">
                <MTooltip v-if="!isInActiveTask">
                  <template v-slot:trigger>
                    <FlotoTaskDrawer
                      :default-task="task"
                      :disabled="disabled"
                      :details-disabled="detailsDisabled"
                      :parent-id="task.parentId"
                      :module-name="task.moduleName"
                      :hide-conversation="isInActiveTask"
                      :hide-work="isInActiveTask"
                      @remove="$emit('remove', task)"
                      @update="$emit('update', $event)"
                    >
                      <template v-slot:trigger="{ open }">
                        <MButton
                          id="view-details-btn"
                          variant="default"
                          class="ml-2 icon-button rounded"
                          @click="open"
                        >
                          <MIcon size="lg" name="eye" />
                        </MButton>
                      </template>
                    </FlotoTaskDrawer>
                  </template>
                  {{ $t('view') }}
                </MTooltip>
                <div
                  v-if="isDone"
                  class="check-icon-button ml-2 rounded"
                  :style="closeStyle"
                >
                  <MIcon name="double-check-alt" size="lg" />
                </div>

                <MTooltip
                  v-else-if="
                    (overDue !== false || dueIn !== false) && !isInActiveTask
                  "
                >
                  <template v-slot:trigger>
                    <a class="cursor-default">
                      <div
                        class="clock-icon-button rounded ml-2"
                        :style="clockStyle"
                      >
                        <MIcon name="clock" size="sm" />
                      </div>
                    </a>
                  </template>
                  {{ overDue || dueIn }} <br />
                  ({{ task.timeRange.endTime | datetime }})
                </MTooltip>
              </slot>
            </div>
            <div class="flex items-center">
              <slot name="task-actions">
                <div
                  v-if="!disabled"
                  class="status-button ml-2 flex items-center cursor-pointer"
                  @click="
                    canReopenTask && isDone
                      ? handleChangeStatus('open')
                      : canCloseTask && !isDone
                      ? handleChangeStatus('closed')
                      : null
                  "
                >
                  <div class="button-content">
                    <MIcon
                      :name="isDone ? 'reopen' : 'double-check-alt'"
                      size="lg"
                      class="text-white"
                    />
                    <span class="button-text text-white">
                      {{ isDone ? $t('reopen') : $t('close') }}
                    </span>
                  </div>
                </div>
              </slot>
              <FlotoDeleteBtn
                v-if="allowTaskDeleteAction && canArchiveTask(task.moduleName)"
                :id="`delete-btn-${task.name}:${task.subject}`"
                size="lg"
                class="mx-1"
                :message="$t('confirm_delete_item', { item: $tc('task') })"
                @confirm="$emit('remove')"
              />
            </div>
          </div>
        </MCol>
      </MRow>
      <FlotoForm>
        <MRow class="mb-1 ml-2">
          <MCol v-if="allowedSystemFields.indexOf('statusId') >= 0" :size="2">
            <FlotoFormItem :label="$t('status')">
              <FlotoStatusPicker
                :value="task.statusId"
                module-name="task"
                :disabled="
                  isDisabledUserToReopenClosedTasks ||
                  isDone ||
                  !canUpdateTaskStatus(task.moduleName) ||
                  singleFieldRulesStateFn('statusId').disable
                "
                :allowed-status="
                  isProjectMilestoneTask ? ['open', 'closed', 'cancelled'] : []
                "
                @change="handleChange({ statusId: $event })"
              >
              </FlotoStatusPicker>
            </FlotoFormItem>
          </MCol>
          <MCol v-if="allowedSystemFields.indexOf('priorityId') >= 0" :size="2">
            <FlotoFormItem :label="$t('priority')">
              <FlotoPriorityPicker
                :value="task.priorityId"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTaskPriority(task.moduleName) ||
                  singleFieldRulesStateFn('priorityId').disable
                "
                @change="handleChange({ priorityId: $event })"
              >
              </FlotoPriorityPicker>
            </FlotoFormItem>
          </MCol>
          <MCol v-if="allowedSystemFields.indexOf('taskTypeId') >= 0" :size="2">
            <FlotoFormItem :label="$tc('task_type')">
              <FlotoTaskTypePicker
                :value="task.taskTypeId"
                :disabled="
                  disabled ||
                  isProjectMilestoneTask ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTask(task.moduleName) ||
                  singleFieldRulesStateFn('taskTypeId').disable
                "
                @change="handleChange({ taskTypeId: $event })"
              >
              </FlotoTaskTypePicker>
            </FlotoFormItem>
          </MCol>
          <MCol v-if="allowedSystemFields.indexOf('groupId') >= 0" :size="2">
            <FlotoFormItem :label="$tc('user_group')">
              <component
                :is="
                  task.moduleName === $constants.PROJECT &&
                  hasProjectRequesterLicense
                    ? 'FlotoUserGroupPicker'
                    : 'FlotoTechnicianGroupPicker'
                "
                :as-input="false"
                :value="task.groupId"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTask(task.moduleName) ||
                  singleFieldRulesStateFn('groupId').disable
                "
                @change="handleChange({ groupId: $event })"
              >
              </component>
            </FlotoFormItem>
          </MCol>
          <MCol
            v-if="allowedSystemFields.indexOf('technicianId') >= 0"
            :size="2"
          >
            <FlotoFormItem :label="$tc('assignee')">
              <component
                :is="
                  task.moduleName === $constants.PROJECT &&
                  hasProjectRequesterLicense
                    ? 'FlotoRequesterPicker'
                    : 'FlotoTechnicianPicker'
                "
                :value="task.technicianId"
                size="default"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canAssignTask(task.moduleName) ||
                  singleFieldRulesStateFn('technicianId').disable
                "
                keep-unassigned
                :group-id="task.groupId"
                change-technician-on-group-change
                @change="handleChange({ technicianId: $event })"
              >
              </component>
            </FlotoFormItem>
          </MCol>
          <MCol
            v-if="
              allowedSystemFields.indexOf('startTime') >= 0 ||
              allowedSystemFields.indexOf('endTime') >= 0
            "
            :size="2"
          >
            <FlotoFormItem
              :label="`${
                allowedSystemFields.indexOf('startTime') >= 0 ? $t('start') : ''
              } ${$t('date')} ${
                allowedSystemFields.indexOf('startTime') >= 0 &&
                allowedSystemFields.indexOf('endTime') >= 0
                  ? ' - '
                  : ''
              } ${
                allowedSystemFields.indexOf('endTime') >= 0 ? $t('end') : ''
              } ${$t('date')}`"
            >
              <div class="ml-2">
                <template
                  v-if="
                    (task.timeRange || {}).startTime ||
                    (task.timeRange || {}).endTime
                  "
                >
                  <span v-if="allowedSystemFields.indexOf('startTime') >= 0">{{
                    (task.timeRange || {}).startTime | datetime
                  }}</span>
                  <span
                    v-if="
                      (task.timeRange || {}).startTime &&
                      (task.timeRange || {}).endTime &&
                      allowedSystemFields.indexOf('endTime') >= 0 &&
                      allowedSystemFields.indexOf('startTime') >= 0
                    "
                    class="text-center my-1"
                  >
                    -
                  </span>
                  <span v-if="allowedSystemFields.indexOf('endTime') >= 0">{{
                    task.timeRange.endTime | datetime
                  }}</span>
                </template>
                <template v-else> --- </template>
              </div>
            </FlotoFormItem>
          </MCol>
          <MCol
            v-if="
              task.moduleName === $constants.PROJECT &&
              allowedSystemFields.indexOf('completion') >= 0
            "
            :size="2"
          >
            <FlotoFormItemReadOnly :label="$tc('completion', 2)">
              {{ task.completion || 0 }} %
            </FlotoFormItemReadOnly>
          </MCol>
          <MCol
            v-if="
              task.moduleName === $constants.PROJECT &&
              allowedSystemFields.indexOf('duration') >= 0
            "
            :size="2"
          >
            <FlotoFormItemReadOnly
              :label="`${$t('duration')} (${$tc('days', 2)})`"
            >
              {{ task.duration || 0 }} {{ $tc('days', task.duration) }}
            </FlotoFormItemReadOnly>
          </MCol>
          <MCol
            v-if="
              task.moduleName === $constants.PROJECT &&
              allowedSystemFields.indexOf('estimatedTime') >= 0
            "
            :size="2"
          >
            <FlotoFormItemReadOnly
              :label="`${$t('estimates')} (${$tc('hours', 2)})`"
            >
              {{ task.estimatedTime || 0 }} {{ $tc('hours', 2) }}
            </FlotoFormItemReadOnly>
          </MCol>
        </MRow>
        <span slot="submit"> </span>
      </FlotoForm>
    </MCol>
  </MRow>
</template>

<script>
import Pick from 'lodash/pick'
import Moment from 'moment'
import { StatusComputed } from '@state/modules/status'
import { TaskComputed } from '@state/modules/task'
import { PreferenceComputed } from '@state/modules/preference'
import { getTagStyleFromColor } from '@/src/utils/color'

import { taskPermissionsMixin } from '@src/mixins/task-permissions'

import { LicenseComputed } from '@state/modules/license'

import { FormComputed } from '@state/modules/form'

import { authComputed } from '@state/modules/auth'

import { getFieldByRequesterAccess } from '@modules/support-portal/helpers/field-access'

export default {
  name: 'TaskDetails',
  mixins: [taskPermissionsMixin],
  inject: {
    formRulesContext: {
      default: () => ({
        fieldRulesState: {},
      }),
    },
  },
  props: {
    task: { type: Object, required: true },
    markBgColor: { type: Boolean, default: false },
    allowEdit: { type: Boolean, default: false },
    // eslint-disable-next-line
    allowTaskHeader: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    isInActiveTask: { type: Boolean, default: false },
    detailsDisabled: { type: Boolean, default: false },
  },
  computed: {
    ...StatusComputed,
    ...TaskComputed,
    ...FormComputed,
    ...authComputed,
    ...Pick(PreferenceComputed, [
      'allowUserToReopenClosedTasks',
      'allowUserToDeleteTasks',
    ]),
    ...Pick(LicenseComputed, ['hasProjectRequesterLicense']),

    allowedSystemFields() {
      let fields = this.taskFields({
        exclude: [],
      })

      if (this.isPortalLogin) {
        fields = getFieldByRequesterAccess(this.taskPortalFields, this.user)
      }

      return fields.filter((f) => f.isSystemField).map((f) => f.paramName)
    },
    isProjectMilestoneTask() {
      const taskTypeId = (
        this.taskTypeOptions.find((t) => t.systemName === 'Milestone') || {}
      ).id
      if (
        this.task.moduleName === this.$constants.PROJECT &&
        this.task.taskTypeId === taskTypeId
      ) {
        return true
      }
      return false
    },
    isDone() {
      const closedStatus = this.taskFindStatus('closed')
      if (closedStatus) {
        return closedStatus.id === this.task.statusId
      }
      return false
    },
    overDue() {
      if (this.isDone) {
        return false
      }
      if (!(this.task.timeRange || {}).endTime) {
        return false
      }
      const diff = Moment().unix() * 1000 - this.task.timeRange.endTime
      if (diff > 0) {
        // return `${this.$t('overdue')} ${this.$t('by')} ${Moment.duration(
        //   diff,
        //   'millisecond'
        // ).humanize()}`
        return `${this.$t('overdue')} ${this.$t(
          'by'
        )} ${this.$options.filters.duration(diff)}`
      }
      return false
    },
    dueIn() {
      if (this.isDone) {
        return false
      }
      if (!(this.task.timeRange || {}).endTime) {
        return false
      }
      const diff = Math.abs(
        Moment().unix() * 1000 - this.task.timeRange.endTime
      )
      // return `${this.$t('due_in')} ${Moment.duration(
      //   diff,
      //   'millisecond'
      // ).humanize()}`
      return `${this.$t('due_in')} ${this.$options.filters.duration(diff)}`
    },
    isDisabledUserToReopenClosedTasks() {
      const currentTask = this.taskStatus.find(
        (f) => f.key === this.task.statusId
      )
      if (this.disabled) {
        return true
      }
      if (this.detailsDisabled) {
        return true
      }
      if (
        currentTask &&
        !this.allowUserToReopenClosedTasks &&
        (currentTask.systemName || '').toLowerCase() === 'closed'
      ) {
        return true
      }
      return false
    },
    allowTaskDeleteAction() {
      if (this.allowUserToDeleteTasks) {
        return !this.disabled && this.allowDelete && !this.detailsDisabled
      }
      return false
    },
    closeStyle() {
      const checkColor = getComputedStyle(
        document.documentElement
      ).getPropertyValue('--primary-button-bg')
      return getTagStyleFromColor(checkColor, this.theme)
    },
    clockStyle() {
      const checkColor = getComputedStyle(
        document.documentElement
      ).getPropertyValue(
        this.overDue ? '--error-button-bg' : '--success-button-bg'
      )
      const borderColor = this.overDue ? '--secondary-red' : '--secondary-green'
      return getTagStyleFromColor(checkColor, this.theme, borderColor)
    },
  },
  methods: {
    handleChange(change) {
      this.$emit('update', { ...this.task, ...change })
    },
    handleChangeStatus(status) {
      const change = {
        statusId: this.taskFindStatus(status).id,
      }
      this.handleChange(change)
    },
    singleFieldRulesStateFn(paramName) {
      if (!paramName) {
        return {}
      }

      const fields = this.taskForm.fields

      const currentField = (fields || []).find((f) => f.paramName === paramName)

      if (this.isPortalLogin) {
        const prepairedRuleState = {
          ...(this.formRulesContext.fieldRulesState || {}),

          [paramName]: {
            ...((this.formRulesContext.fieldRulesState || {})[paramName] || {}),

            disable:
              !(currentField || {}).requesterCanEdit ||
              ((this.formRulesContext.fieldRulesState || {})[paramName] || {})
                .disable,
          },
        }

        return (prepairedRuleState || {})[paramName] || {}
      } else {
        return (this.formRulesContext.fieldRulesState || {})[paramName] || {}
      }
    },
  },
}
</script>

<style lang="less" scoped>
.heading {
  position: relative;
  padding-right: 0;

  .mark-icon {
    position: absolute;
    top: 0;
    right: 5px;
  }
}

.status-button {
  @apply rounded;

  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  padding: 4px 6px;
  background-color: var(--primary);
  transition: all 0.3s ease-in-out;
  animation: fade-in 0.3s ease-in-out;

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .button-content {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  .button-text {
    max-width: 0;
    margin-left: 0;
    overflow: hidden;
    opacity: 0;
    transition: all 0.3s ease-in-out;
  }

  &:hover {
    .button-text {
      max-width: 200px;
      margin-left: 8px;
      opacity: 1;
      animation: text-fade 0.3s ease-in-out;
    }
  }
}

.whitespace-no-wrap {
  white-space: nowrap;
}

@keyframes text-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
